import { useEffect, useState, type FormEvent, type ReactElement } from "react";

import { api, ApiError } from "../../api/client";
import { SkeletonRows } from "../Skeleton";
import type { QuestionnaireOut } from "../../api/types";

// Full one-time demographics/health questionnaire (legacy one_time parity,
// docs/archive/DESIGN.md §12a). `sex` is sent as the enum value so the API mirrors it onto
// participants.sex_at_birth; everything else rides the opaque payload (schema v2).

const SCHEMA_VERSION = "v2";

// Free-text length caps (mirrored server-side in QuestionnaireSubmit).
const MAX_TEXT = 100;
const MAX_DETAIL = 300;
// The API caps other_conditions at 20 entries — stop offering "Add another".
const MAX_OTHER_CONDITIONS = 20;

export const GENDERS = [
  ["female", "Female"],
  ["male", "Male"],
  ["trans_male", "Transgender male"],
  ["trans_female", "Transgender female"],
  ["non_binary", "Non-binary"],
  ["gnc", "Genderqueer / gender non-conforming"],
  ["prefer_not", "Prefer not to say"],
  ["other", "Other"],
] as const;

export const RACES = [
  ["american_indian", "American Indian / Alaska Native"],
  ["asian", "Asian"],
  ["black", "Black / African American"],
  ["pacific_islander", "Native Hawaiian / Pacific Islander"],
  ["white", "White"],
  ["mena", "Middle Eastern / North African"],
  ["multiracial", "Multiracial"],
  ["other", "Other"],
] as const;

export const ETHNICITIES = [
  ["hispanic", "Hispanic or Latino"],
  ["not_hispanic", "Not Hispanic or Latino"],
  ["unknown", "Unknown / prefer not to answer"],
] as const;

export const SMOKING = [
  ["never", "Never smoker"],
  ["former", "Former smoker"],
  ["current", "Current smoker"],
] as const;

export const CONDITIONS = [
  ["alzheimers", "Alzheimer's"],
  ["anxiety", "Anxiety"],
  ["arthritis", "Arthritis"],
  ["asthma", "Asthma"],
  ["cancer", "Cancer"],
  ["cardiovascular", "Cardiovascular disease"],
  ["ckd", "Chronic kidney disease"],
  ["copd", "COPD"],
  ["crohns", "Crohn's"],
  ["cystic_fibrosis", "Cystic fibrosis"],
  ["depression", "Depression"],
  ["epilepsy", "Epilepsy"],
  ["hyperlipidemia", "Hyperlipidemia"],
  ["hypertension", "Hypertension"],
  ["obesity", "Obesity"],
  ["osteoporosis", "Osteoporosis"],
  ["stroke", "Stroke"],
  ["type2_diabetes", "Type 2 diabetes"],
] as const;

interface ConditionEntry {
  age: string;
  extra?: string; // cancer type
}

export function OneTimeQuestionnaire({
  active,
  onSubmitted,
}: {
  active: boolean;
  onSubmitted?: () => void;
}): ReactElement {
  const [done, setDone] = useState(false);
  const [checking, setChecking] = useState(true);

  const [sex, setSex] = useState("");
  const [gender, setGender] = useState("");
  const [genderOther, setGenderOther] = useState("");
  const [race, setRace] = useState("");
  const [raceOther, setRaceOther] = useState("");
  const [ethnicity, setEthnicity] = useState("");
  const [smoking, setSmoking] = useState("");
  const [etohYn, setEtohYn] = useState("");
  const [weeklyEtoh, setWeeklyEtoh] = useState("");
  const [cosmetic, setCosmetic] = useState("");
  const [cosmeticDetail, setCosmeticDetail] = useState("");
  const [pmh, setPmh] = useState("");
  const [noConditions, setNoConditions] = useState(false);
  const [conditions, setConditions] = useState<Record<string, ConditionEntry>>({});
  // Free-text conditions not in the list — repeatable ("Add another").
  const [others, setOthers] = useState<{ name: string; age: string }[]>([{ name: "", age: "" }]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const rows = await api<QuestionnaireOut[]>("GET", "/participants/me/questionnaires");
        if (rows.some((r) => r.questionnaire_type === "one_time")) setDone(true);
      } catch {
        // non-fatal; fall through to the form
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  function setOther(index: number, patch: Partial<{ name: string; age: string }>): void {
    setOthers((prev) => prev.map((o, i) => (i === index ? { ...o, ...patch } : o)));
  }

  function toggleCondition(id: string): void {
    setNoConditions(false);
    setConditions((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = { age: "" };
      return next;
    });
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const othersFilled = others.filter((o) => o.name.trim());
    if (pmh === "yes" && !noConditions && Object.keys(conditions).length === 0 && othersFilled.length === 0) {
      setError(
        'Please select your medical conditions (or check "I do not have any of these conditions").',
      );
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        sex,
        gender,
        ...(gender === "other" ? { gender_other: genderOther } : {}),
        race,
        ...(race === "other" || race === "multiracial" ? { race_other: raceOther } : {}),
        ethnicity,
        smoking,
        alcohol: etohYn,
        ...(etohYn === "yes" ? { weekly_drinks: Number(weeklyEtoh) } : {}),
        cosmetic_procedures: cosmetic,
        ...(cosmetic === "yes" ? { cosmetic_detail: cosmeticDetail } : {}),
        disclose_conditions: pmh,
        ...(pmh === "yes"
          ? {
              conditions: noConditions
                ? []
                : Object.entries(conditions).map(([id, entry]) => ({
                    condition: id,
                    age_of_diagnosis: entry.age ? Number(entry.age) : null,
                    ...(id === "cancer" && entry.extra ? { cancer_type: entry.extra } : {}),
                  })),
              ...(othersFilled.length && !noConditions
                ? {
                    other_conditions: othersFilled.map((o) => ({
                      condition: o.name.trim(),
                      age_of_diagnosis: o.age ? Number(o.age) : null,
                    })),
                  }
                : {}),
            }
          : {}),
        // No self-reported substudy: partner-study membership is structural
        // (study-code → substudy link) — the checkbox was heavily mis-clicked.
        // Legacy payloads keep their migrated value.
      };
      await api<{ instance_number: number }>("POST", "/participants/me/questionnaire", {
        json: { questionnaire_type: "one_time", payload, schema_version: SCHEMA_VERSION },
      });
      setDone(true);
      onSubmitted?.();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setDone(true);
        onSubmitted?.();
      } else {
        setError(err instanceof ApiError ? err.message : "Could not submit the questionnaire.");
      }
    } finally {
      setBusy(false);
    }
  }

  if (checking) return <SkeletonRows rows={3} />;
  if (done) {
    return (
      <p className="muted">
        <i className="fas fa-check-circle status-success" /> Initial questionnaire
        complete.
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="q-form">
      <p className="muted">Complete this once. It helps us track FaceAge performance across demographics and health status</p>

      <Select label="Sex assigned at birth" value={sex} onChange={setSex} required>
        <option value="female">Female</option>
        <option value="male">Male</option>
        <option value="intersex">Intersex</option>
      </Select>

      <Select label="Gender identity" value={gender} onChange={setGender} required options={GENDERS} />
      {gender === "other" ? (
        <Text label="Please specify your gender identity" value={genderOther} onChange={setGenderOther} placeholder="Enter gender identity" required />
      ) : null}

      <Select label="Race" value={race} onChange={setRace} required options={RACES} />
      {race === "other" || race === "multiracial" ? (
        <Text label="Please specify" value={raceOther} onChange={setRaceOther} placeholder="Enter race" required />
      ) : null}

      <Select label="Ethnicity" value={ethnicity} onChange={setEthnicity} required options={ETHNICITIES} />
      <Select label="Smoking history" value={smoking} onChange={setSmoking} required options={SMOKING} />

      <Select label="Do you consume alcohol?" value={etohYn} onChange={setEtohYn} required>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </Select>
      {etohYn === "yes" ? (
        <label className="field">
          <span>Approximate alcoholic drinks per week</span>
          <input
            type="number"
            min={1}
            max={99}
            step={1}
            required
            placeholder="Enter number of drinks"
            value={weeklyEtoh}
            onChange={(e) => setWeeklyEtoh(e.target.value)}
          />
          <div className="field-hint">
            One &quot;standard&quot; drink (or one alcoholic drink equivalent) contains roughly 14
            grams of pure alcohol, which is found in:
            <ul>
              <li>12 fluid ounces of regular beer (usually about 5% alcohol)</li>
              <li>5 fluid ounces of wine (typically about 12% alcohol)</li>
              <li>1.5 fluid ounces of distilled spirits (about 40% alcohol)</li>
            </ul>
          </div>
        </label>
      ) : null}

      <Select label="History of cosmetic procedures?" value={cosmetic} onChange={setCosmetic} required>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </Select>
      {cosmetic === "yes" ? (
        <label className="field">
          <span>Please list them (comma-separated)</span>
          <textarea
            rows={2}
            required
            placeholder="Please list all cosmetic procedures separated by commas"
            value={cosmeticDetail}
            maxLength={MAX_DETAIL}
            onChange={(e) => setCosmeticDetail(e.target.value)}
          />
          <div className="field-hint">
            Cosmetic procedures include but are not limited to: surgical (e.g., rhinoplasty, breast
            augmentation, liposuction) and non-surgical procedures (e.g., Botox, dermal fillers,
            laser treatments).
          </div>
        </label>
      ) : null}

      <Select label="Willing to disclose medical conditions?" value={pmh} onChange={setPmh} required>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </Select>
      {pmh === "yes" ? (
        <fieldset className="consent-block">
          <legend>Select all that apply</legend>
          <label className="choice">
            <input
              type="checkbox"
              checked={noConditions}
              onChange={() => {
                setNoConditions((v) => !v);
                setConditions({});
              }}
            />
            I do not have any of these conditions
          </label>
          {!noConditions ? (
            <div className="condition-grid">
              {CONDITIONS.map(([id, label]) => (
                <div key={id} className="condition-item">
                  <label className="choice">
                    <input type="checkbox" checked={!!conditions[id]} onChange={() => toggleCondition(id)} />
                    {label}
                  </label>
                  {conditions[id] ? (
                    <div className="condition-detail">
                      <input
                        type="number"
                        min={0}
                        max={120}
                        step={1}
                        placeholder="Age at diagnosis"
                        value={conditions[id].age}
                        onChange={(e) =>
                          setConditions((p) => ({
                            ...p,
                            [id]: { age: e.target.value, extra: p[id]?.extra },
                          }))
                        }
                      />
                      {id === "cancer" ? (
                        <input
                          type="text"
                          placeholder="Type of cancer"
                          maxLength={MAX_TEXT}
                          value={conditions[id].extra ?? ""}
                          onChange={(e) =>
                            setConditions((p) => ({
                              ...p,
                              [id]: { age: p[id]?.age ?? "", extra: e.target.value },
                            }))
                          }
                        />
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ))}
              <div className="condition-item">
                {others.map((o, i) => (
                  <div key={i}>
                    <Text
                      label={i === 0 ? "Other condition (optional)" : "Another condition"}
                      value={o.name}
                      onChange={(v) => setOther(i, { name: v })}
                    />
                    {o.name ? (
                      <div className="condition-detail">
                        <input
                          type="number"
                          min={0}
                          max={120}
                          step={1}
                          placeholder="Age at diagnosis"
                          value={o.age}
                          onChange={(e) => setOther(i, { age: e.target.value })}
                        />
                      </div>
                    ) : null}
                  </div>
                ))}
                {others[others.length - 1]?.name && others.length < MAX_OTHER_CONDITIONS ? (
                  <button
                    type="button"
                    className="btn-ghost btn-sm"
                    onClick={() => setOthers((prev) => [...prev, { name: "", age: "" }])}
                  >
                    Add another condition
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </fieldset>
      ) : null}

      {error ? <p className="form-error">{error}</p> : null}
      <button type="submit" className="btn-primary-glow" disabled={!active || busy}>
        {busy ? "Submitting…" : "Submit questionnaire"}
      </button>
    </form>
  );
}

// ── small field helpers ───────────────────────────────────────────────────────
function Select({
  label,
  value,
  onChange,
  required,
  options,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  options?: readonly (readonly [string, string])[];
  children?: ReactElement | ReactElement[];
}): ReactElement {
  return (
    <label className="field">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} required={required}>
        <option value="">Select…</option>
        {options ? options.map(([v, l]) => <option key={v} value={v}>{l}</option>) : children}
      </select>
    </label>
  );
}

function Text({
  label,
  value,
  onChange,
  maxLength = MAX_TEXT,
  required,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  maxLength?: number;
  required?: boolean;
  placeholder?: string;
}): ReactElement {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        type="text"
        value={value}
        maxLength={maxLength}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
